/**
 * ParagraphSpacing — a toolbar toggle that removes the vertical spacing of the
 * selected paragraph(s), similar to MS Word's "No Spacing" / paragraph-zero.
 *
 * It works by writing an INLINE style (margin-top/bottom: 0) onto the <p>, plus a
 * data-spacing marker for reliable round-tripping. The inline margin is what makes
 * this survive the export: Aspose.Words maps inline paragraph margins to Word
 * paragraph spacing (it ignores CSS classes, but honours inline styles).
 */
import { Plugin, Command } from '@ckeditor/ckeditor5-core';
import { ButtonView } from '@ckeditor/ckeditor5-ui';

const ATTR = 'paragraphSpacing';
const NO_SPACING = 'none';

// Word-style "line & paragraph spacing" glyph: vertical double arrow + text lines.
const ICON =
	'<svg viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">' +
	'<path fill="currentColor" d="M8 3h10v1.7H8zM8 7h10v1.7H8zM8 11h10v1.7H8zM8 15h10v1.7H8z"/>' +
	'<path fill="currentColor" d="M4 1.8l2.6 3.7h-5.2zM4 18.2l-2.6-3.7h5.2zM3.15 5h1.7v10h-1.7z"/></svg>';

class ParagraphSpacingCommand extends Command {
	declare public value: boolean;

	public override refresh(): void {
		const model = this.editor.model;
		const blocks = Array.from( model.document.selection.getSelectedBlocks() )
			.filter( block => model.schema.checkAttribute( block, ATTR ) );

		this.isEnabled = blocks.length > 0;
		this.value = blocks.length > 0 && blocks.every( block => block.getAttribute( ATTR ) === NO_SPACING );
	}

	public override execute(): void {
		const model = this.editor.model;

		model.change( writer => {
			const blocks = Array.from( model.document.selection.getSelectedBlocks() )
				.filter( block => model.schema.checkAttribute( block, ATTR ) );
			const turnOn = !this.value;

			for ( const block of blocks ) {
				if ( turnOn ) {
					writer.setAttribute( ATTR, NO_SPACING, block );
				} else {
					writer.removeAttribute( ATTR, block );
				}
			}
		} );
	}
}

export default class ParagraphSpacing extends Plugin {
	public static get pluginName() {
		return 'ParagraphSpacing' as const;
	}

	public init(): void {
		const editor = this.editor;

		editor.model.schema.extend( 'paragraph', { allowAttributes: ATTR } );
		editor.commands.add( 'paragraphSpacing', new ParagraphSpacingCommand( editor ) );

		// Model attribute -> inline style + marker (runs for both editing and data pipelines).
		editor.conversion.for( 'downcast' ).add( dispatcher => {
			dispatcher.on( `attribute:${ ATTR }:paragraph`, ( evt, data, conversionApi ) => {
				const viewElement = conversionApi.mapper.toViewElement( data.item );

				if ( !viewElement ) {
					return;
				}

				const writer = conversionApi.writer;

				if ( data.attributeNewValue === NO_SPACING ) {
					writer.setStyle( 'margin-top', '0', viewElement );
					writer.setStyle( 'margin-bottom', '0', viewElement );
					writer.setAttribute( 'data-spacing', 'none', viewElement );
				} else {
					writer.removeStyle( 'margin-top', viewElement );
					writer.removeStyle( 'margin-bottom', viewElement );
					writer.removeAttribute( 'data-spacing', viewElement );
				}
			} );
		} );

		// Marker -> model attribute, so the setting survives save/reload.
		editor.conversion.for( 'upcast' ).attributeToAttribute( {
			view: { key: 'data-spacing', value: 'none' },
			model: { key: ATTR, value: NO_SPACING }
		} );

		editor.ui.componentFactory.add( 'paragraphSpacing', locale => {
			const command = editor.commands.get( 'paragraphSpacing' ) as ParagraphSpacingCommand;
			const button = new ButtonView( locale );

			button.set( {
				label: 'No paragraph spacing',
				icon: ICON,
				tooltip: true,
				isToggleable: true
			} );

			button.bind( 'isOn' ).to( command, 'value' );
			button.bind( 'isEnabled' ).to( command, 'isEnabled' );

			button.on( 'execute', () => {
				editor.execute( 'paragraphSpacing' );
				editor.editing.view.focus();
			} );

			return button;
		} );
	}
}
